import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  DataTable,
  DataTablePagination,
  defineTableSchema,
  useConfiguredTable,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { useFilters } from '@art-of-hospitality-solutions-llc/ui-kit/components/filters';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useTagAndSegment } from '../../contexts/tagAndSegment';
import service from '../../service';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useAuth } from '../../contexts/auth';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import EditTagModal from '../EditTagModal';
import ShowFor from '@Components/ShowFor';

export const tagColumnConfigs = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={
          row.getIsSelected()
            ? true
            : row.getIsSomeSelected?.()
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    type: 'boolean',
    size: 50,
  },
  {
    id: 'name',
    headerLabel: 'Tag Name',
    accessor: (row) => {
      return row?.name ?? 'N/A';
    },
    type: 'text',
    size: 300,
  },
  {
    id: 'createdByUser',
    headerLabel: 'Created By',
    accessor: (row) => row?.createdByUser?.email ?? 'N/A',
    type: 'text',
    size: 120,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created At',
    accessor: (row) => row.createdAt ?? '',
    type: 'date',
    cell: (info) => {
      const date = info.getValue();
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || 'N/A';
    },
  },
];

const TagList = () => {
  const { readAllTags, tagArray } = useTagAndSegment();
  const { user } = useAuth();

  const { filters, addOrUpdateFilter, removeFilter } = useFilters([]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnPinning, setColumnPinning] = useState({
    left: ['select', 'name'],
    right: [],
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editInfo, setEditInfo] = useState({ id: null, name: '' });

  const columnConfigs = tagColumnConfigs;

  const { columns, filters: filterDefinitions } =
    defineTableSchema(columnConfigs);

  const sortBy = 'createdAt';
  const fallbackData = [];

  const { table } = useConfiguredTable({
    data: tagArray ?? fallbackData,
    columns,
    filters,
    initialSorting: [{ id: sortBy, desc: true }],
    pagination: {
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    columnPinning,
    setColumnPinning,
    autoResetPageOnFilterChange: true,
  });

  const handleEdit = () => {
    const selectedRow = table
      .getSelectedRowModel()
      .rows.map((row) => row?.original?.id);
    if (selectedRow.length !== 1) return;
    setEditInfo({ ...editInfo, id: selectedRow[0] });
    setIsEditing(true);
  };

  const handleUpdateTag = async () => {
    try {
      await service.updateTag(editInfo.id, {
        name: editInfo.name,
      });
      setEditInfo({
        id: null,
        name: '',
      });
      await readAllTags();
      toastSuccess('Tag updated successfully');
      table.toggleAllPageRowsSelected(false);
      setIsEditing(false);
    } catch (error) {
      toastError('Failed to update tag: ' + error.message);
    }
  };

  const closeEditModal = () => {
    setEditInfo({
      id: null,
      name: '',
    });
    setIsEditing(false);
  };

  const handleDeleteSelected = () => {
    if (table.getSelectedRowModel().rows.length) {
      setIsDeleteModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    try {
      const tagIds = table
        .getSelectedRowModel()
        .rows.map((row) => row?.original?.id);
      await service.deleteManyTags(tagIds, user.id);
      table.toggleAllPageRowsSelected(false);
      setIsDeleteModalOpen(false);
      await readAllTags();
      toastSuccess(`Successfully deleted ${tagIds.length} tag(s)`);
    } catch (error) {
      toastError('Failed to delete tags: ' + error.message);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  useEffect(() => {
    readAllTags();
  }, [tagArray?.length]);

  return (
    <div>
      <ShowFor permissions={['TAGS_WRITE']}>
        <div className="mb-2 flex w-full gap-4">
          <Button
            onClick={handleEdit}
            disabled={table?.getSelectedRowModel()?.rows?.length !== 1}
            className={`${table?.getSelectedRowModel()?.rows?.length !== 1
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : ''
              } px-4 py-2`}
          >
            Edit
          </Button>

          <Button
            onClick={handleDeleteSelected}
            disabled={!table.getSelectedRowModel().rows.length}
            className={`${!table.getSelectedRowModel().rows.length
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : 'bg-red-500 text-white hover:bg-red-600'
              } px-4 py-2`}
          >
            Delete
          </Button>
        </div>
      </ShowFor>

      <div className="flex items-center gap-4 py-2">
        <Input
          placeholder="Filter by name..."
          value={filters.find((f) => f.field === 'name')?.value ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value) {
              addOrUpdateFilter({
                field: 'name',
                operator: 'contains',
                value,
                displayName: 'Name',
                displayValue: value,
              });
            } else {
              removeFilter('name');
            }
          }}
          className="w-64"
        />

        <Input
          placeholder="Filter by Creator..."
          value={filters.find((f) => f.field === 'createdByUser')?.value ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value) {
              addOrUpdateFilter({
                field: 'createdByUser',
                operator: 'contains',
                value,
                displayName: 'Creator',
                displayValue: value,
              });
            } else {
              removeFilter('createdByUser');
            }
          }}
          className="w-64"
        />
      </div>

      <DataTable
        table={table}
        onRowClick={(row) => {
          row.toggleSelected(!row.getIsSelected());
        }}
        skeletonRowCount={pageSize}
      />
      <DataTablePagination
        table={table}
        meta={{ total: tagArray?.length }}
        pagination={{ size: pageSize, number: pageIndex + 1 }}
        setPage={({ size, number }) => {
          setPageSize(size);
          setPageIndex(number - 1);
        }}
        setPageNumber={(n) => setPageIndex(n - 1)}
      />

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          onConfirm={confirmDelete}
          onClose={closeDeleteModal}
          itemCount={table.getSelectedRowModel().rows.length}
          itemType="tag"
        />
      )}

      {isEditing && (
        <EditTagModal
          onSave={handleUpdateTag}
          onClose={closeEditModal}
          editInfo={editInfo}
          setEditInfo={setEditInfo}
        />
      )}
    </div>
  );
};

export default TagList;
